/**
 * Piano Roll Editor Component
 * MIDI note editor with grid-based editing
 */
import { useState, useRef, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../ui/button';
import { Slider } from '../ui/slider';
import {
  X,
  ZoomIn,
  ZoomOut,
  Trash2,
  Copy,
  Scissors,
  ClipboardPaste,
  Grid3X3,
  Play,
  Pause,
  Magnet,
} from 'lucide-react';
import { NOTE_NAMES, midiToNoteName } from './MIDIEngine';
import {
  createMIDINote,
  addNoteToClip,
  removeNoteFromClip,
  moveNoteInClip,
  resizeNoteInClip,
  updateNoteInClip,
  quantizeClipNotes,
  getGridSize,
  QUANTIZE_VALUES,
} from './MIDIClip';

// Piano roll configuration
const NOTE_HEIGHT = 16;
const MIN_NOTE_PITCH = 24; // C1
const MAX_NOTE_PITCH = 96; // C7
const TOTAL_NOTES = MAX_NOTE_PITCH - MIN_NOTE_PITCH + 1;
const KEY_WIDTH = 60;

// Note colors based on pitch class
const getNoteColor = (pitch) => {
  const colors = [
    'bg-red-500', 'bg-red-400', 'bg-orange-500', 'bg-orange-400',
    'bg-yellow-500', 'bg-green-500', 'bg-green-400', 'bg-cyan-500',
    'bg-blue-500', 'bg-blue-400', 'bg-purple-500', 'bg-purple-400',
  ];
  return colors[pitch % 12];
};

// Check if note is black key
const isBlackKey = (pitch) => {
  const noteInOctave = pitch % 12;
  return [1, 3, 6, 8, 10].includes(noteInOctave);
};

export const PianoRoll = ({
  clip,
  onClipUpdate,
  onClose,
  bpm = 120,
  timeSignature = 4,
  isPlaying = false,
  currentTime = 0,
  onPlayNote,
  onStopNote,
  onUndo,
  onRedo,
  canUndo = false,
  canRedo = false,
}) => {
  const [zoom, setZoom] = useState(1);
  const [scrollX, setScrollX] = useState(0);
  const [scrollY, setScrollY] = useState((MAX_NOTE_PITCH - 60) * NOTE_HEIGHT); // Start at middle C
  const [selectedNotes, setSelectedNotes] = useState(new Set());
  const [quantizeValue, setQuantizeValue] = useState('1/16');
  const [snapToGrid, setSnapToGrid] = useState(true);
  const [tool, setTool] = useState('select'); // 'select', 'draw', 'erase'
  const [dragging, setDragging] = useState(null);
  const [hoveredNote, setHoveredNote] = useState(null);
  const [clipboard, setClipboard] = useState([]); // Copied notes
  
  const containerRef = useRef(null);
  const gridRef = useRef(null);
  const [isFocused, setIsFocused] = useState(true); // Piano roll starts focused when opened
  
  // Calculate dimensions
  const pixelsPerSecond = 100 * zoom;
  const gridWidth = clip ? clip.duration * pixelsPerSecond : 800;
  const gridHeight = TOTAL_NOTES * NOTE_HEIGHT;
  const gridSize = getGridSize(bpm, quantizeValue);
  
  // Convert pixel position to time
  const pixelToTime = useCallback((x) => {
    return x / pixelsPerSecond;
  }, [pixelsPerSecond]);
  
  // Convert time to pixel position
  const timeToPixel = useCallback((time) => {
    return time * pixelsPerSecond;
  }, [pixelsPerSecond]);
  
  // Convert pixel Y to pitch
  const pixelToPitch = useCallback((y) => {
    return MAX_NOTE_PITCH - Math.floor(y / NOTE_HEIGHT);
  }, []);
  
  // Convert pitch to pixel Y
  const pitchToPixel = useCallback((pitch) => {
    return (MAX_NOTE_PITCH - pitch) * NOTE_HEIGHT;
  }, []);
  
  // Snap time to grid if enabled
  const snapTime = useCallback((time) => {
    if (!snapToGrid) return time;
    return Math.round(time / gridSize) * gridSize;
  }, [snapToGrid, gridSize]);
  
  // Handle mouse down on grid (add note or select)
  const handleGridMouseDown = useCallback((e) => {
    if (!clip) return;
    
    const rect = gridRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left + scrollX;
    const y = e.clientY - rect.top + scrollY;
    
    const time = snapTime(pixelToTime(x));
    const pitch = pixelToPitch(y);
    
    if (pitch < MIN_NOTE_PITCH || pitch > MAX_NOTE_PITCH) return;
    
    if (tool === 'draw') {
      // Add new note
      const newNote = createMIDINote({
        pitch,
        startTime: time,
        duration: gridSize,
        velocity: 100,
      });
      
      const updatedClip = addNoteToClip(clip, newNote);
      onClipUpdate?.(updatedClip, {
        type: 'ADD_NOTE',
        noteId: newNote.id,
        note: newNote,
      });
      
      // Play note preview
      onPlayNote?.(pitch, 100);
      setTimeout(() => onStopNote?.(pitch), 200);
      
      // Start dragging to resize
      setDragging({
        type: 'resize',
        noteId: newNote.id,
        startX: e.clientX,
        startTime: time,
        originalDuration: gridSize,
      });
    } else if (tool === 'erase') {
      // Erase notes at position
      const noteAtPos = clip.notes.find(n => 
        time >= n.startTime && 
        time < n.startTime + n.duration &&
        pitch === n.pitch
      );
      
      if (noteAtPos) {
        const updatedClip = removeNoteFromClip(clip, noteAtPos.id);
        onClipUpdate?.(updatedClip, {
          type: 'REMOVE_NOTE',
          noteId: noteAtPos.id,
          note: noteAtPos,
        });
      }
    } else {
      // Select mode - clear selection if clicking empty space
      setSelectedNotes(new Set());
    }
  }, [clip, tool, scrollX, scrollY, snapTime, pixelToTime, pixelToPitch, gridSize, onClipUpdate, onPlayNote, onStopNote]);
  
  // Handle mouse down on note
  const handleNoteMouseDown = useCallback((e, note) => {
    e.stopPropagation();
    
    if (tool === 'erase') {
      const updatedClip = removeNoteFromClip(clip, note.id);
      onClipUpdate?.(updatedClip, {
        type: 'REMOVE_NOTE',
        noteId: note.id,
        note,
      });
      return;
    }
    
    // Toggle selection with shift
    if (e.shiftKey) {
      setSelectedNotes(prev => {
        const next = new Set(prev);
        if (next.has(note.id)) {
          next.delete(note.id);
        } else {
          next.add(note.id);
        }
        return next;
      });
      return;
    }
    
    // Alt+Drag to duplicate note
    if (e.altKey) {
      // Create a copy of the note
      const newNote = createMIDINote({
        pitch: note.pitch,
        startTime: note.startTime,
        duration: note.duration,
        velocity: note.velocity,
      });
      
      const updatedClip = addNoteToClip(clip, newNote);
      onClipUpdate?.(updatedClip, {
        type: 'ADD_NOTE',
        noteId: newNote.id,
        note: newNote,
      });
      
      // Start dragging the new note (the original stays in place)
      setDragging({
        type: 'move',
        noteId: newNote.id,
        startX: e.clientX,
        startY: e.clientY,
        originalTime: note.startTime,
        originalPitch: note.pitch,
        isDuplicate: true,
      });
      
      // Select the new note
      setSelectedNotes(new Set([newNote.id]));
      
      // Play note preview
      onPlayNote?.(note.pitch, note.velocity);
      return;
    }
    
    // Start dragging
    const rect = gridRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left + scrollX;
    const noteX = timeToPixel(note.startTime);
    const noteWidth = timeToPixel(note.duration);
    
    // Check if clicking on resize handle (right edge)
    if (x > noteX + noteWidth - 10) {
      setDragging({
        type: 'resize',
        noteId: note.id,
        startX: e.clientX,
        startTime: note.startTime,
        originalDuration: note.duration,
      });
    } else {
      // Move note
      setDragging({
        type: 'move',
        noteId: note.id,
        startX: e.clientX,
        startY: e.clientY,
        originalTime: note.startTime,
        originalPitch: note.pitch,
      });
      
      // Play note preview
      onPlayNote?.(note.pitch, note.velocity);
    }
    
    // Select this note
    if (!selectedNotes.has(note.id)) {
      setSelectedNotes(new Set([note.id]));
    }
  }, [clip, tool, scrollX, selectedNotes, timeToPixel, onClipUpdate, onPlayNote]);
  
  // Handle mouse move (dragging)
  const handleMouseMove = useCallback((e) => {
    if (!dragging || !clip) return;
    
    const deltaX = e.clientX - dragging.startX;
    
    if (dragging.type === 'resize') {
      const deltaTime = pixelToTime(deltaX);
      const newDuration = Math.max(gridSize, snapTime(dragging.originalDuration + deltaTime));
      
      const updatedClip = resizeNoteInClip(clip, dragging.noteId, newDuration);
      onClipUpdate?.(updatedClip, null); // Don't create undo action while dragging
    } else if (dragging.type === 'move') {
      const deltaY = e.clientY - dragging.startY;
      const deltaTime = pixelToTime(deltaX);
      const deltaPitch = -Math.round(deltaY / NOTE_HEIGHT);
      
      const newTime = snapTime(Math.max(0, dragging.originalTime + deltaTime));
      const newPitch = Math.max(MIN_NOTE_PITCH, Math.min(MAX_NOTE_PITCH, dragging.originalPitch + deltaPitch));
      
      const updatedClip = moveNoteInClip(clip, dragging.noteId, {
        startTime: newTime,
        pitch: newPitch,
      });
      onClipUpdate?.(updatedClip, null);
    }
  }, [dragging, clip, pixelToTime, gridSize, snapTime, onClipUpdate]);
  
  // Handle mouse up (end dragging)
  const handleMouseUp = useCallback((e) => {
    if (dragging && clip) {
      // Find the note and create undo action
      const note = clip.notes.find(n => n.id === dragging.noteId);
      if (note) {
        if (dragging.type === 'resize') {
          onClipUpdate?.(clip, {
            type: 'RESIZE_NOTE',
            noteId: dragging.noteId,
            oldDuration: dragging.originalDuration,
            newDuration: note.duration,
          });
        } else if (dragging.type === 'move') {
          onClipUpdate?.(clip, {
            type: 'MOVE_NOTE',
            noteId: dragging.noteId,
            oldTime: dragging.originalTime,
            oldPitch: dragging.originalPitch,
            newTime: note.startTime,
            newPitch: note.pitch,
          });
          
          // Stop note preview
          onStopNote?.(note.pitch);
        }
      }
    }
    setDragging(null);
  }, [dragging, clip, onClipUpdate, onStopNote]);
  
  // Handle velocity change
  const handleVelocityChange = useCallback((noteId, velocity) => {
    if (!clip) return;
    
    const note = clip.notes.find(n => n.id === noteId);
    if (!note) return;
    
    const updatedClip = updateNoteInClip(clip, noteId, { velocity });
    onClipUpdate?.(updatedClip, {
      type: 'UPDATE_VELOCITY',
      noteId,
      oldVelocity: note.velocity,
      newVelocity: velocity,
    });
  }, [clip, onClipUpdate]);
  
  // Quantize selected notes
  const handleQuantize = useCallback(() => {
    if (!clip || selectedNotes.size === 0) return;
    
    // Store old values for undo
    const oldNotes = clip.notes.filter(n => selectedNotes.has(n.id));
    
    let updatedClip = { ...clip };
    oldNotes.forEach(note => {
      const quantized = quantizeClipNotes({ notes: [note] }, bpm, quantizeValue, 1);
      updatedClip = updateNoteInClip(updatedClip, note.id, {
        startTime: quantized.notes[0].startTime,
      });
    });
    
    onClipUpdate?.(updatedClip, {
      type: 'QUANTIZE_NOTES',
      noteIds: Array.from(selectedNotes),
      oldTimes: oldNotes.map(n => ({ id: n.id, startTime: n.startTime })),
    });
  }, [clip, selectedNotes, bpm, quantizeValue, onClipUpdate]);
  
  // Delete selected notes
  const handleDelete = useCallback(() => {
    if (!clip || selectedNotes.size === 0) return;
    
    const deletedNotes = clip.notes.filter(n => selectedNotes.has(n.id));
    let updatedClip = { ...clip };
    
    selectedNotes.forEach(id => {
      updatedClip = removeNoteFromClip(updatedClip, id);
    });
    
    onClipUpdate?.(updatedClip, {
      type: 'DELETE_NOTES',
      notes: deletedNotes,
    });
    
    setSelectedNotes(new Set());
  }, [clip, selectedNotes, onClipUpdate]);
  
  // Duplicate selected notes
  const handleDuplicateNotes = useCallback(() => {
    if (!clip || selectedNotes.size === 0) return;
    
    const notesToDuplicate = clip.notes.filter(n => selectedNotes.has(n.id));
    if (notesToDuplicate.length === 0) return;
    
    // Find the rightmost note's end position
    const maxEndTime = Math.max(...notesToDuplicate.map(n => n.startTime + n.duration));
    const minStartTime = Math.min(...notesToDuplicate.map(n => n.startTime));
    const offset = maxEndTime - minStartTime + gridSize; // Place after the last note
    
    let updatedClip = { ...clip };
    const newNoteIds = [];
    
    notesToDuplicate.forEach(note => {
      const newNote = createMIDINote({
        pitch: note.pitch,
        startTime: note.startTime + offset,
        duration: note.duration,
        velocity: note.velocity,
      });
      updatedClip = addNoteToClip(updatedClip, newNote);
      newNoteIds.push(newNote.id);
    });
    
    onClipUpdate?.(updatedClip, {
      type: 'DUPLICATE_NOTES',
      sourceNotes: notesToDuplicate,
      newNoteIds,
    });
    
    // Select the new notes
    setSelectedNotes(new Set(newNoteIds));
  }, [clip, selectedNotes, gridSize, onClipUpdate]);
  
  // Copy selected notes to clipboard
  const handleCopy = useCallback(() => {
    if (!clip || selectedNotes.size === 0) return;
    
    const notesToCopy = clip.notes.filter(n => selectedNotes.has(n.id));
    if (notesToCopy.length === 0) return;
    
    // Store relative positions (normalized to the earliest note)
    const minStartTime = Math.min(...notesToCopy.map(n => n.startTime));
    
    const clipboardNotes = notesToCopy.map(note => ({
      pitch: note.pitch,
      startTime: note.startTime - minStartTime, // Relative position
      duration: note.duration,
      velocity: note.velocity,
    }));
    
    setClipboard(clipboardNotes);
    
    // Visual feedback - could add a toast here
    return clipboardNotes.length;
  }, [clip, selectedNotes]);
  
  // Cut selected notes (copy + delete)
  const handleCut = useCallback(() => {
    if (!clip || selectedNotes.size === 0) return;
    
    // Copy first
    const copiedCount = handleCopy();
    if (!copiedCount) return;
    
    // Then delete
    const deletedNotes = clip.notes.filter(n => selectedNotes.has(n.id));
    let updatedClip = { ...clip };
    
    selectedNotes.forEach(id => {
      updatedClip = removeNoteFromClip(updatedClip, id);
    });
    
    onClipUpdate?.(updatedClip, {
      type: 'CUT_NOTES',
      notes: deletedNotes,
    });
    
    setSelectedNotes(new Set());
  }, [clip, selectedNotes, handleCopy, onClipUpdate]);
  
  // Paste notes from clipboard
  const handlePaste = useCallback(() => {
    if (!clip || clipboard.length === 0) return;
    
    // Paste at the current playhead position or at the end of existing notes
    let pasteTime = 0;
    if (clip.notes.length > 0) {
      // Paste after the last selected note, or after all notes if nothing selected
      if (selectedNotes.size > 0) {
        const selectedNotesList = clip.notes.filter(n => selectedNotes.has(n.id));
        pasteTime = Math.max(...selectedNotesList.map(n => n.startTime + n.duration));
      } else {
        pasteTime = Math.max(...clip.notes.map(n => n.startTime + n.duration));
      }
    }
    
    // Snap paste time to grid
    pasteTime = snapToGrid ? Math.round(pasteTime / gridSize) * gridSize : pasteTime;
    
    let updatedClip = { ...clip };
    const newNoteIds = [];
    
    clipboard.forEach(noteData => {
      const newNote = createMIDINote({
        pitch: noteData.pitch,
        startTime: pasteTime + noteData.startTime,
        duration: noteData.duration,
        velocity: noteData.velocity,
      });
      updatedClip = addNoteToClip(updatedClip, newNote);
      newNoteIds.push(newNote.id);
    });
    
    onClipUpdate?.(updatedClip, {
      type: 'PASTE_NOTES',
      pastedNotes: clipboard,
      newNoteIds,
    });
    
    // Select the pasted notes
    setSelectedNotes(new Set(newNoteIds));
  }, [clip, clipboard, selectedNotes, snapToGrid, gridSize, onClipUpdate]);
  
  // Zoom in
  const handleZoomIn = useCallback(() => {
    setZoom(prev => Math.min(4, prev + 0.25));
  }, []);
  
  // Zoom out
  const handleZoomOut = useCallback(() => {
    setZoom(prev => Math.max(0.25, prev - 0.25));
  }, []);
  
  // Select all notes
  const handleSelectAll = useCallback(() => {
    if (clip) {
      setSelectedNotes(new Set(clip.notes.map(n => n.id)));
    }
  }, [clip]);
  
  // Keyboard shortcuts - only active when piano roll is focused
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Only respond when piano roll is focused
      if (!isFocused) return;
      
      // Tool shortcuts (no modifiers)
      if (!e.metaKey && !e.ctrlKey && !e.altKey) {
        switch (e.key.toLowerCase()) {
          case 'v':
            e.preventDefault();
            setTool('select');
            return;
          case 'p':
            e.preventDefault();
            setTool('draw');
            return;
          case 'e':
            e.preventDefault();
            setTool('erase');
            return;
          case 'q':
            e.preventDefault();
            handleQuantize();
            return;
          default:
            break;
        }
      }
      
      // Delete/Backspace - delete selected notes
      if (e.key === 'Delete' || e.key === 'Backspace') {
        // Don't prevent if user is in an input field
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
        e.preventDefault();
        handleDelete();
        return;
      }
      
      // Escape - close piano roll
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose?.();
        return;
      }
      
      // Ctrl/Cmd shortcuts
      if (e.metaKey || e.ctrlKey) {
        switch (e.key.toLowerCase()) {
          case 'a':
            // Select all
            e.preventDefault();
            handleSelectAll();
            return;
          case 'z':
            // Undo/Redo
            e.preventDefault();
            if (e.shiftKey) {
              onRedo?.();
            } else {
              onUndo?.();
            }
            return;
          case 'd':
            // Duplicate selected notes
            e.preventDefault();
            handleDuplicateNotes();
            return;
          case 'c':
            // Copy selected notes
            e.preventDefault();
            handleCopy();
            return;
          case 'x':
            // Cut selected notes
            e.preventDefault();
            handleCut();
            return;
          case 'v':
            // Paste notes (Ctrl+V - different from 'v' without modifier for Select tool)
            e.preventDefault();
            handlePaste();
            return;
          default:
            break;
        }
      }
    };
    
    // Handle wheel zoom with Ctrl/Cmd
    const handleWheel = (e) => {
      if (!isFocused) return;
      
      if (e.metaKey || e.ctrlKey) {
        e.preventDefault();
        if (e.deltaY < 0) {
          handleZoomIn();
        } else {
          handleZoomOut();
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('wheel', handleWheel, { passive: false });
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('wheel', handleWheel);
    };
  }, [isFocused, handleDelete, handleQuantize, handleSelectAll, handleDuplicateNotes, handleCopy, handleCut, handlePaste, handleZoomIn, handleZoomOut, onUndo, onRedo, onClose, clip]);
  
  // Mouse event listeners for dragging
  useEffect(() => {
    if (dragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [dragging, handleMouseMove, handleMouseUp]);
  
  if (!clip) {
    return (
      <div className="flex items-center justify-center h-full text-zinc-500">
        Select a MIDI clip to edit
      </div>
    );
  }
  
  // Render grid lines
  const renderGridLines = () => {
    const lines = [];
    const secondsPerBeat = 60 / bpm;
    
    // Vertical lines (time)
    for (let t = 0; t <= clip.duration; t += gridSize) {
      const x = timeToPixel(t);
      const isBeat = Math.abs(t % secondsPerBeat) < 0.01;
      const isBar = Math.abs(t % (secondsPerBeat * timeSignature)) < 0.01;
      
      lines.push(
        <line
          key={`v-${t}`}
          x1={x}
          y1={0}
          x2={x}
          y2={gridHeight}
          stroke={isBar ? '#444' : isBeat ? '#333' : '#222'}
          strokeWidth={isBar ? 2 : 1}
        />
      );
    }
    
    // Horizontal lines (pitch)
    for (let p = MIN_NOTE_PITCH; p <= MAX_NOTE_PITCH; p++) {
      const y = pitchToPixel(p);
      const isC = p % 12 === 0;
      
      lines.push(
        <line
          key={`h-${p}`}
          x1={0}
          y1={y}
          x2={gridWidth}
          y2={y}
          stroke={isC ? '#444' : '#222'}
          strokeWidth={isC ? 2 : 1}
        />
      );
    }
    
    return lines;
  };
  
  // Render notes
  const renderNotes = () => {
    return clip.notes.map(note => {
      const x = timeToPixel(note.startTime);
      const y = pitchToPixel(note.pitch);
      const width = Math.max(4, timeToPixel(note.duration) - 2);
      const isSelected = selectedNotes.has(note.id);
      const isHovered = hoveredNote === note.id;
      
      return (
        <g key={note.id}>
          <rect
            x={x + 1}
            y={y + 1}
            width={width}
            height={NOTE_HEIGHT - 2}
            rx={3}
            className={`${getNoteColor(note.pitch)} cursor-pointer transition-opacity ${
              isSelected ? 'opacity-100' : 'opacity-80'
            }`}
            fill={`hsl(${(note.pitch * 5) % 360}, 70%, ${50 + (note.velocity / 127) * 20}%)`}
            stroke={isSelected ? '#fff' : isHovered ? '#888' : 'transparent'}
            strokeWidth={isSelected ? 2 : 1}
            onMouseDown={(e) => handleNoteMouseDown(e, note)}
            onMouseEnter={() => setHoveredNote(note.id)}
            onMouseLeave={() => setHoveredNote(null)}
          />
          {/* Velocity indicator */}
          <rect
            x={x + 1}
            y={y + NOTE_HEIGHT - 4}
            width={(width * note.velocity) / 127}
            height={2}
            fill="rgba(255,255,255,0.5)"
            pointerEvents="none"
          />
          {/* Resize handle */}
          {isSelected && (
            <rect
              x={x + width - 6}
              y={y + 1}
              width={6}
              height={NOTE_HEIGHT - 2}
              fill="rgba(255,255,255,0.3)"
              className="cursor-ew-resize"
            />
          )}
        </g>
      );
    });
  };
  
  // Render playhead
  const renderPlayhead = () => {
    if (!isPlaying) return null;
    
    const playheadX = timeToPixel(currentTime - clip.startTime);
    if (playheadX < 0 || playheadX > gridWidth) return null;
    
    return (
      <line
        x1={playheadX}
        y1={0}
        x2={playheadX}
        y2={gridHeight}
        stroke="#ef4444"
        strokeWidth={2}
      />
    );
  };
  
  return (
    <div 
      className="flex flex-col h-full bg-zinc-950 outline-none" 
      data-testid="piano-roll"
      tabIndex={0}
      onFocus={() => setIsFocused(true)}
      onBlur={(e) => {
        // Only unfocus if focus is leaving the piano roll entirely
        if (!e.currentTarget.contains(e.relatedTarget)) {
          setIsFocused(false);
        }
      }}
      ref={(el) => {
        // Auto-focus when mounted
        if (el) el.focus();
      }}
    >
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 bg-zinc-900 border-b border-zinc-800">
        <div className="flex items-center gap-4">
          <span className="text-sm font-medium">{clip.name}</span>
          
          {/* Tool Selection */}
          <div className="flex items-center gap-1 bg-zinc-800 rounded-lg p-1">
            <Button
              variant={tool === 'select' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setTool('select')}
              className={`h-7 px-2 ${tool === 'select' ? 'bg-indigo-500' : ''}`}
              title="Select (V)"
            >
              Select
            </Button>
            <Button
              variant={tool === 'draw' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setTool('draw')}
              className={`h-7 px-2 ${tool === 'draw' ? 'bg-green-500' : ''}`}
              title="Draw (P)"
            >
              Draw
            </Button>
            <Button
              variant={tool === 'erase' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setTool('erase')}
              className={`h-7 px-2 ${tool === 'erase' ? 'bg-red-500' : ''}`}
              title="Erase (E)"
            >
              Erase
            </Button>
          </div>
          
          {/* Snap to Grid */}
          <Button
            variant={snapToGrid ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSnapToGrid(!snapToGrid)}
            className={`h-7 ${snapToGrid ? 'bg-purple-500' : 'border-zinc-700'}`}
            title="Snap to Grid"
          >
            <Magnet className="w-4 h-4 mr-1" />
            Snap
          </Button>
          
          {/* Quantize */}
          <div className="flex items-center gap-2">
            <select
              value={quantizeValue}
              onChange={(e) => setQuantizeValue(e.target.value)}
              className="h-7 px-2 bg-zinc-800 border border-zinc-700 rounded text-sm"
              data-testid="quantize-select"
            >
              {Object.keys(QUANTIZE_VALUES).map(v => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
            <Button
              variant="outline"
              size="sm"
              onClick={handleQuantize}
              disabled={selectedNotes.size === 0}
              className="h-7 border-zinc-700"
              title="Quantize Selected (Q)"
              data-testid="quantize-btn"
            >
              <Grid3X3 className="w-4 h-4 mr-1" />
              Quantize
            </Button>
          </div>
        </div>
        
        <div className="flex items-center gap-1">
          {/* Clipboard Actions */}
          <div className="flex items-center gap-0.5 border-r border-zinc-700 pr-2 mr-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCopy}
              disabled={selectedNotes.size === 0}
              className="h-7 px-2 text-zinc-400 hover:text-white"
              title="Copy (Ctrl+C)"
              data-testid="copy-btn"
            >
              <Copy className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCut}
              disabled={selectedNotes.size === 0}
              className="h-7 px-2 text-zinc-400 hover:text-white"
              title="Cut (Ctrl+X)"
              data-testid="cut-btn"
            >
              <Scissors className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handlePaste}
              disabled={clipboard.length === 0}
              className="h-7 px-2 text-zinc-400 hover:text-white"
              title="Paste (Ctrl+V)"
              data-testid="paste-btn"
            >
              <ClipboardPaste className="w-4 h-4" />
            </Button>
          </div>
          
          {/* Zoom */}
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleZoomOut}
            title="Zoom Out (Ctrl+Scroll Down)"
          >
            <ZoomOut className="w-4 h-4" />
          </Button>
          <span className="text-xs w-12 text-center">{Math.round(zoom * 100)}%</span>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleZoomIn}
            title="Zoom In (Ctrl+Scroll Up)"
          >
            <ZoomIn className="w-4 h-4" />
          </Button>
          
          {/* Delete */}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDelete}
            disabled={selectedNotes.size === 0}
            className="text-red-400 hover:text-red-300"
            title="Delete Selected (Del/Backspace)"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
          
          {/* Close */}
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-zinc-400 hover:text-white"
            title="Close (Esc)"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>
      
      {/* Piano Roll Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Piano Keys */}
        <div 
          className="flex-shrink-0 bg-zinc-900 border-r border-zinc-800 overflow-hidden"
          style={{ width: KEY_WIDTH }}
        >
          <div 
            className="relative"
            style={{ 
              height: gridHeight,
              transform: `translateY(-${scrollY}px)`,
            }}
          >
            {Array.from({ length: TOTAL_NOTES }, (_, i) => {
              const pitch = MAX_NOTE_PITCH - i;
              const isBlack = isBlackKey(pitch);
              
              return (
                <div
                  key={pitch}
                  className={`absolute left-0 right-0 flex items-center justify-end pr-2 text-xs border-b border-zinc-800 ${
                    isBlack 
                      ? 'bg-zinc-800 text-zinc-400' 
                      : 'bg-zinc-700 text-zinc-200'
                  }`}
                  style={{
                    top: i * NOTE_HEIGHT,
                    height: NOTE_HEIGHT,
                  }}
                  onClick={() => {
                    onPlayNote?.(pitch, 100);
                    setTimeout(() => onStopNote?.(pitch), 200);
                  }}
                >
                  {pitch % 12 === 0 && midiToNoteName(pitch)}
                </div>
              );
            })}
          </div>
        </div>
        
        {/* Grid and Notes */}
        <div 
          ref={containerRef}
          className="flex-1 overflow-auto"
          onScroll={(e) => {
            setScrollX(e.currentTarget.scrollLeft);
            setScrollY(e.currentTarget.scrollTop);
          }}
        >
          <svg
            ref={gridRef}
            width={gridWidth}
            height={gridHeight}
            className="bg-zinc-950"
            onMouseDown={handleGridMouseDown}
          >
            {/* Background for black keys */}
            {Array.from({ length: TOTAL_NOTES }, (_, i) => {
              const pitch = MAX_NOTE_PITCH - i;
              if (!isBlackKey(pitch)) return null;
              
              return (
                <rect
                  key={`bg-${pitch}`}
                  x={0}
                  y={i * NOTE_HEIGHT}
                  width={gridWidth}
                  height={NOTE_HEIGHT}
                  fill="rgba(0,0,0,0.3)"
                />
              );
            })}
            
            {/* Grid lines */}
            {renderGridLines()}
            
            {/* Notes */}
            {renderNotes()}
            
            {/* Playhead */}
            {renderPlayhead()}
          </svg>
        </div>
      </div>
      
      {/* Status Bar */}
      <div className="px-4 py-1 bg-zinc-900 border-t border-zinc-800 text-xs text-zinc-500 flex justify-between">
        <div className="flex items-center gap-4">
          <span>{clip.notes.length} notes • {selectedNotes.size} selected{clipboard.length > 0 ? ` • ${clipboard.length} in clipboard` : ''}</span>
          <span className="text-zinc-600">
            V/P/E: Tools • ⌘+C/X/V: Copy/Cut/Paste • ⌘+A: Select All • Del: Delete • Q: Quantize • Esc: Close
          </span>
        </div>
        <span>{bpm} BPM • Grid: {quantizeValue}</span>
      </div>
    </div>
  );
};

export default PianoRoll;
